import {useEffect, useMemo} from 'react';
import {useQueries, UseQueryResult} from '@tanstack/react-query';
import {isEqual} from 'lodash';

import {useDocumentContext} from '@/components/document/context';
import {useDocumentStore} from '@/components/document/store';
import {loadTemplate, DocumentModel, ValueResolver, VisibilitySet} from '@/engine';
import {getMappings} from '@/engine/renderer/pdf/template';
import {SupportedLocale} from '@/initializers/i18next';
import {DocumentForm, DocumentFormType} from '@/interfaces/document';
import {generateForm, getForm, removeForm} from '@/repositories/beneva-api/endpoints/documents';
import {arrayBufferToBase64} from '@/utils/buffer';
import {isNotFoundError} from '@/utils/error-handling';

const generateFormQueryFn =
  (documentModel: DocumentModel, valueResolver: ValueResolver, visibilitySet: VisibilitySet) =>
  (documentId: string, formId: string, locale: SupportedLocale) =>
  async () => {
    const documentForm = documentModel.documents.find(form => form.type === formId);
    if (!documentForm) throw new Error(`Form with id ${formId} not found in model`);
    const template = await loadTemplate(documentForm.filename, locale);
    const base64string = arrayBufferToBase64(template);
    const {mappings, replacements, stamps, signatures} = await getMappings(
      template,
      documentForm.mappers ?? [],
      documentForm.replacements ?? {},
      valueResolver,
      visibilitySet,
      locale,
    );

    const {data} = await generateForm(documentId, {
      fileName: `${documentId}-${documentForm.type}-${locale}.pdf`,
      type: formId as DocumentFormType,
      content: base64string,
      mappings,
      replacements,
      stamps,
      signatures,
    });

    return data;
  };

type DocumentFormQuery = {
  included: boolean;
  type: string;
  query: UseQueryResult<DocumentForm | null, Error>;
};

export const useDocumentForms = (): DocumentFormQuery[] => {
  const {document, documentModel, valueResolver, visibilitySet, forms: documentForms, setForms} = useDocumentContext();
  const locale = useDocumentStore(state => state.locale);

  const queryFn = useMemo(() => {
    return generateFormQueryFn(documentModel, valueResolver, visibilitySet);
  }, [documentModel, valueResolver, visibilitySet]);

  const queries = useQueries({
    queries: documentModel.documents.map(({type, included}) => ({
      queryKey: ['form', document.id, type, locale],
      staleTime: 0,
      refetchOnWindowFocus: false,
      queryFn: async () => {
        const alreadyGenerated = documentForms.find(form => form.type === type);

        if (!included) {
          if (alreadyGenerated) {
            // Remove previously generated form if it exists
            await removeForm(document.id, alreadyGenerated.id);
          }
          return null;
        }

        try {
          // Generate form if it is included
          return await queryFn(document.id, type, locale)();
        } catch (error) {
          if (alreadyGenerated && isNotFoundError(error)) {
            const {data} = await getForm(document.id, alreadyGenerated.id);
            return data;
          }
          throw error;
        }
      },
    })),
  });

  const forms = queries.map((query, index) => {
    const {included, type} = documentModel.documents[index];
    return {included, type, query};
  });

  const includedForms = useMemo(() => forms.filter(doc => doc.included), [forms]);

  const newForms = useMemo(() => {
    if (!includedForms.filter(doc => doc.included).every(doc => doc.query.isSuccess)) return;
    return includedForms
      .filter(doc => doc.included && doc.query.isSuccess && doc.query.data !== null)
      .map(doc => doc.query.data as DocumentForm);
  }, [includedForms]);

  const formsHasChanged = useMemo(() => {
    if (!newForms) return false;
    return !isEqual(
      documentForms.map(doc => doc.id),
      newForms.map(doc => doc.id),
    );
  }, [newForms, documentForms]);

  useEffect(() => {
    if (newForms && formsHasChanged) {
      // Update the generated forms in the context
      setForms(newForms);
    }
  }, [setForms, formsHasChanged, newForms]);

  return includedForms;
};
